import React from 'react';
import {
  Search, Share2, Layers, Check, Cpu, UserCheck, Palette, BarChart3,
  ShieldCheck, Rocket, Sparkles,
} from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  PM_ECOSYSTEM, PM_SUMMARY, PM_CARDS, PM_FEE_ROWS, PM_ONBOARDING,
  PM_ONBOARDING_INCLUDES, PM_AI_ANALYSIS, PM_HUMAN_STRATEGY,
  PM_CREATIVE_INCLUDES, PM_REPORTING, PM_EXPECTATION_FACTORS, PM_PROCESS,
} from '../data/paidMediaData';
import { trackEvent } from '../lib/track';

const CARD_ICONS = { 'google-ads': Search, 'meta-ads': Share2, 'google-meta': Layers };

const goToEnquiry = (projectType, event) => {
  if (event) trackEvent(event, { location: 'paid_media' });
  if (projectType) {
    window.dispatchEvent(new CustomEvent('boost-prefill-enquiry', { detail: { project_type: projectType } }));
  }
  document.querySelector('#enquiry')?.scrollIntoView({ behavior: 'smooth' });
};

function Transition() {
  return (
    <section className="pp-section pp-pm-intro" id="paid-media">
      <div className="pp-container">
        <div className="pp-section-head">
          <span className="pp-kicker">Accelerate growth</span>
          <h2 className="pp-h2 pp-display">BE THERE WHEN YOUR CUSTOMERS ARE <em>READY TO ACT.</em></h2>
          <p className="pp-lead">
            Organic visibility builds long-term authority. Paid media gives businesses the ability to
            reach potential customers immediately.
          </p>
          <p className="pp-lead">
            Boost! plans, builds and manages performance-focused advertising campaigns across Google,
            Facebook and Instagram — connecting campaign data, creative strategy and commercial
            objectives to make every advertising pound work harder.
          </p>
          <p className="pp-lead">
            Our approach combines continuous AI-assisted campaign analysis with human strategy, creative
            judgement and ongoing optimisation.
          </p>
        </div>

        <div className="pp-pm-ecosystem" data-testid="pm-ecosystem">
          {PM_ECOSYSTEM.map((step, i) => (
            <React.Fragment key={step}>
              <span className="pp-pm-eco-step">{step}</span>
              {i < PM_ECOSYSTEM.length - 1 && <span className="pp-pm-eco-arrow">→</span>}
            </React.Fragment>
          ))}
        </div>
        <p className="pp-small-note" style={{ textAlign: 'center' }}>
          Available as a standalone service or alongside a Boost! website and SEO + GEO programme.
        </p>
      </div>
    </section>
  );
}

function ServiceCards() {
  return (
    <section className="pp-section" style={{ paddingTop: 0 }}>
      <div className="pp-container">
        <div className="pp-cards pp-pm-cards">
          {PM_CARDS.map((c) => {
            const Icon = CARD_ICONS[c.id];
            return (
              <article
                className={`pp-card ${c.recommended ? 'pp-card-popular' : ''}`}
                data-testid={`pm-card-${c.id}`}
                key={c.id}
              >
                {c.recommended && <span className="pp-popular-badge">Recommended</span>}
                <span className="pp-pm-card-icon"><Icon size={22} /></span>
                <h3 className="pp-card-title pp-display">{c.title}</h3>
                <div className="pp-card-price">{c.priceEx} <small>{c.period}</small></div>
                <p className="pp-card-inc">{c.priceInc}</p>
                <span className="pp-card-delivery">{c.onboarding}</span>
                <p className="pp-card-desc">{c.description}</p>
                {c.includesHeading && <p className="pp-pm-inc-heading">{c.includesHeading}</p>}
                <ul className="pp-card-list">
                  {c.includes.map((item) => <li key={item}><Check size={15} /> {item}</li>)}
                </ul>
                {c.note && <p className="pp-card-note">{c.note}</p>}
                <button
                  className={`pp-btn ${c.recommended ? 'pp-btn-primary' : 'pp-btn-outline'}`}
                  onClick={() => goToEnquiry(c.projectType, c.event)}
                  data-testid={`pm-cta-${c.id}`}
                >
                  {c.cta}
                </button>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Fees() {
  return (
    <section className="pp-section pp-section-alt">
      <div className="pp-container">
        <Accordion
          type="single"
          collapsible
          className="pp-pm-fees"
          onValueChange={(v) => { if (v) trackEvent('paid_media_fees_expand', {}); }}
        >
          <AccordionItem value="fees" className="pp-faq-item" data-testid="pm-fees">
            <AccordionTrigger className="pp-faq-trigger pp-pm-fees-trigger">
              Transparent management fees
            </AccordionTrigger>
            <AccordionContent className="pp-faq-content">
              <p className="pp-lead" style={{ marginTop: 8 }}>
                Your advertising budget and Boost!&rsquo;s management fee are completely separate.
                Advertising spend is paid directly by the client to Google or Meta. Boost! charges a
                predictable monthly management fee based on the scale and complexity of the programme.
              </p>

              <div className="pp-table-wrap pp-pm-fee-table">
                <table className="pp-table">
                  <thead>
                    <tr><th>Monthly advertising spend</th><th>Google OR Meta</th><th>Google + Meta</th></tr>
                  </thead>
                  <tbody>
                    {PM_FEE_ROWS.map((r) => (
                      <tr key={r.spend}>
                        <td>{r.spend}</td>
                        <td>{r.single} <span className="pp-pm-inc">{r.singleInc}</span></td>
                        <td>{r.dual} <span className="pp-pm-inc">{r.dualInc}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pp-pm-fee-cards">
                {PM_FEE_ROWS.map((r) => (
                  <div className="pp-pm-fee-card" key={r.spend}>
                    <span className="pp-pm-fee-spend">{r.spend}</span>
                    <div className="pp-pm-fee-line"><span>Google OR Meta</span><strong>{r.single} <em>{r.singleInc}</em></strong></div>
                    <div className="pp-pm-fee-line"><span>Google + Meta</span><strong>{r.dual} <em>{r.dualInc}</em></strong></div>
                  </div>
                ))}
              </div>

              <p className="pp-small-note">
                Final management fees may vary where accounts contain unusually large numbers of
                campaigns, markets, product feeds, locations or conversion journeys.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  );
}

function Onboarding() {
  return (
    <section className="pp-section">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">GET THE FOUNDATIONS <em>RIGHT.</em></h2>
          <p className="pp-lead">
            Paid-media campaigns depend on accurate tracking, clear objectives and sensible account structure.
          </p>
        </div>
        <div className="pp-pm-onboarding">
          {PM_ONBOARDING.map((o) => (
            <article className="pp-card" data-testid={`pm-onboarding-${o.id}`} key={o.id}>
              <h3 className="pp-card-title pp-display">{o.title}</h3>
              <p className="pp-card-ideal">{o.scope}</p>
              <div className="pp-card-price">{o.priceEx}</div>
              <p className="pp-card-inc">{o.priceInc}</p>
              <span className="pp-card-delivery">{o.launch}</span>
            </article>
          ))}
        </div>
        <div className="pp-pm-onboarding-inc">
          <h4 className="pp-pm-subhead">Onboarding can include</h4>
          <div className="pp-chips">
            {PM_ONBOARDING_INCLUDES.map((s) => <span className="pp-chip" key={s}>{s}</span>)}
          </div>
          <p className="pp-small-note">
            Complex ecommerce feeds, server-side tracking, significant Analytics / Tag Manager remediation
            or unusual technical integrations may require additional setup work and will be quoted before
            commencement.
          </p>
        </div>
      </div>
    </section>
  );
}

function AiHuman() {
  return (
    <section className="pp-section pp-section-alt">
      <div className="pp-container">
        <div className="pp-section-head">
          <span className="pp-kicker">Technology + judgement</span>
          <h2 className="pp-h2 pp-display">ALWAYS-ON ANALYSIS. <em>HUMAN DECISIONS.</em></h2>
          <p className="pp-lead">
            Modern advertising platforms generate enormous amounts of data. Boost! uses intelligent
            analysis to continuously examine campaign performance, identify anomalies, highlight
            opportunities and support faster decision-making.
          </p>
          <p className="pp-lead">
            But automation does not understand every commercial consideration. Margin, stock,
            seasonality, customer quality, creative direction and wider business priorities still
            require human judgement — so every Boost! advertising programme combines intelligent
            technology with human campaign oversight.
          </p>
        </div>
        <div className="pp-pm-duo">
          <div className="pp-pm-duo-col" data-testid="pm-ai-analysis">
            <span className="pp-pm-card-icon"><Cpu size={22} /></span>
            <h4>Intelligent analysis</h4>
            <ul className="pp-card-list">
              {PM_AI_ANALYSIS.map((s) => <li key={s}><Check size={15} /> {s}</li>)}
            </ul>
          </div>
          <div className="pp-pm-duo-col pp-pm-duo-human" data-testid="pm-human-strategy">
            <span className="pp-pm-card-icon"><UserCheck size={22} /></span>
            <h4>Human strategy</h4>
            <ul className="pp-card-list">
              {PM_HUMAN_STRATEGY.map((s) => <li key={s}><Check size={15} /> {s}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Creative() {
  return (
    <section className="pp-section">
      <div className="pp-container">
        <div className="pp-pm-creative" data-testid="pm-creative">
          <div>
            <span className="pp-pm-card-icon"><Palette size={22} /></span>
            <h3 className="pp-h2 pp-display" style={{ fontSize: '1.6rem' }}>PAID MEDIA <em>CREATIVE.</em></h3>
            <p className="pp-lead">
              Strong advertising needs strong creative. Boost! can also design campaign-specific
              advertising assets for Meta, Google Display and other paid-media placements.
            </p>
            <p className="pp-card-price" style={{ fontSize: '1.4rem' }}>Creative support from £295 + VAT</p>
            <button
              className="pp-btn pp-btn-primary"
              onClick={() => goToEnquiry('Paid Media Creative', 'paid_media_creative_click')}
              data-testid="pm-creative-cta"
            >
              Discuss campaign creative
            </button>
          </div>
          <div>
            <h4 className="pp-pm-subhead">Can include</h4>
            <ul className="pp-card-list">
              {PM_CREATIVE_INCLUDES.map((s) => <li key={s}><Check size={15} /> {s}</li>)}
            </ul>
            <p className="pp-small-note">
              Photography, UGC, animation and video production are quoted separately.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Reporting() {
  return (
    <section className="pp-section pp-section-alt">
      <div className="pp-container">
        <div className="pp-section-head">
          <span className="pp-pm-card-icon" style={{ margin: '0 auto 16px' }}><BarChart3 size={22} /></span>
          <h2 className="pp-h2 pp-display">KNOW WHERE YOUR MONEY IS <em>GOING.</em></h2>
          <p className="pp-lead">
            Clients retain full access to their advertising accounts and campaign data. Reporting
            focuses on commercial performance rather than vanity metrics.
          </p>
        </div>
        <div className="pp-chips" data-testid="pm-reporting">
          {PM_REPORTING.map((s) => <span className="pp-chip" key={s}>{s}</span>)}
        </div>
        <p className="pp-small-note">
          Boost! will clearly separate advertising spend from management fees in all proposals and reporting.
        </p>
      </div>
    </section>
  );
}

function Ownership() {
  return (
    <section className="pp-section">
      <div className="pp-container">
        <div className="pp-pm-ownership" data-testid="pm-ownership">
          <span className="pp-pm-card-icon"><ShieldCheck size={22} /></span>
          <h2 className="pp-h2 pp-display">YOUR ACCOUNT. <em>YOUR DATA.</em></h2>
          <p className="pp-lead">
            Wherever practical, advertising campaigns are operated within client-owned advertising
            accounts. The client retains ownership of their account, campaign history, audience data
            and billing relationship with the advertising platform.
          </p>
          <p className="pp-lead">
            Advertising spend is paid directly by the client to Google, Meta or the relevant advertising
            provider. Boost! does not apply a hidden markup to client advertising spend.
          </p>
        </div>
      </div>
    </section>
  );
}

function Expectations() {
  return (
    <section className="pp-section pp-section-alt">
      <div className="pp-container">
        <div className="pp-turnaround">
          <div>
            <h2 className="pp-h2 pp-display">A PROCESS OF TESTING AND <em>OPTIMISATION.</em></h2>
            <p className="pp-lead">
              Strong campaign performance develops through data, testing and continued optimisation.
              Boost! does not guarantee a particular return on advertising spend, lead volume, revenue
              level, cost per acquisition or other performance result.
            </p>
            <div className="pp-priority-note">
              <strong>Recommended commitment:</strong> we recommend an initial three-month management
              period so campaigns have time to launch, gather meaningful data, test opportunities and
              refine performance. After that, services may move to a monthly rolling arrangement subject
              to the agreed terms.
            </div>
          </div>
          <div>
            <h4 className="pp-pm-subhead">Results depend on</h4>
            <ul className="pp-factors">
              {PM_EXPECTATION_FACTORS.map((f) => <li key={f}><Check size={15} /> {f}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function PaidMediaProcess() {
  return (
    <section className="pp-section">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">THE PAID MEDIA <em>PROCESS.</em></h2>
        </div>
        <div className="pp-process" data-testid="pm-process">
          {PM_PROCESS.map((s) => (
            <div className="pp-step" key={s.step}>
              <span className="pp-step-num">{s.step}</span>
              <h4>{s.title}</h4>
              <p>{s.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalBand() {
  return (
    <section className="pp-pm-band" data-testid="pm-final-cta">
      <div className="pp-container">
        <Sparkles size={30} style={{ marginBottom: 14 }} />
        <h2 className="pp-display">TURN CLICKS INTO <span>CUSTOMERS.</span></h2>
        <p>
          Whether you&rsquo;re launching paid advertising for the first time or looking to improve an
          existing account, Boost! can build a campaign strategy around your business, objectives and
          available budget.
        </p>
        <div className="pp-pm-band-ctas">
          <button
            className="pp-btn pp-pm-band-btn"
            onClick={() => goToEnquiry('Google + Meta Ads Management', 'combined_paid_media_cta_click')}
            data-testid="pm-band-primary"
          >
            Request a Paid Media proposal
          </button>
          <button
            className="pp-btn pp-pm-band-btn-outline"
            onClick={() => goToEnquiry('Existing advertising account audit', 'paid_media_account_review_click')}
            data-testid="pm-band-secondary"
          >
            Ask for an account review
          </button>
        </div>
      </div>
    </section>
  );
}

function SummaryStrip() {
  return (
    <section className="pp-section" style={{ paddingBottom: 0 }}>
      <div className="pp-container">
        <div className="pp-pm-summary" data-testid="pm-summary">
          {PM_SUMMARY.map((s) => (
            <div className="pp-pm-summary-item" key={s.name}>
              <span className="pp-pm-summary-name">{s.name}</span>
              <span className="pp-pm-summary-price">{s.price}</span>
            </div>
          ))}
        </div>
        <p className="pp-small-note" style={{ textAlign: 'center' }}>
          <Rocket size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          A full-service growth partner — from website to paid media and ongoing growth.
        </p>
      </div>
    </section>
  );
}

export default function PaidMedia() {
  return (
    <>
      <Transition />
      <ServiceCards />
      <Fees />
      <Onboarding />
      <AiHuman />
      <Creative />
      <Reporting />
      <Ownership />
      <Expectations />
      <PaidMediaProcess />
      <SummaryStrip />
      <FinalBand />
    </>
  );
}
